import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Input, message, Select, Space, Table } from "antd";
import { Drawer } from "antd";
import { Modal, Radio } from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { getDocuments } from "../../../../apis/schemeService";
import { IPreAuthQuery } from "../../../../interfaces/IPreAuthQuery";
import { addQuery, fetchQueries } from "../../../../models/preAuthQuerySlice";
import { fetchStepperData } from "../../../../models/preAuthSlice";
import { errorMessage } from "../../../../utils/notificationUtils";
import {
  getBasicDetails,
  getTimelineDetails,
} from "../../../../models/patientSlice";
import { getAllStarhealthDocumentsData } from "../../../../apis/patientService";
import { errorMessageModal } from "../../../../utils/modalMessage";

const { Option } = Select;

type QueryIhxProps = {
  createQuery: boolean;
  closeCreateQueryModal: any;
  activeStage: any;
};

type RequiredDocument = {
  id: any;
  name: string;
  expiryDate: string;
};

type DocumentInfo = {
  id: string;
  value: string;
};

const QueryIhx: FC<QueryIhxProps> = ({
  createQuery,
  closeCreateQueryModal,
  activeStage,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { patients, auth, hospital, preAuth } = useSelector(
    (state: RootState) => state
  );
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
  const [selectedQueryStage, setSelectedQueryStage] = useState<any>(undefined);
  const [queryText, setQueryText] = useState<string>("");
  const [typeOfQuery, setTypeOfQuery] = useState<string>("Internal");
  const [assignToFrontOffice, setAssignToFrontOffice] = useState<string>("");
  const [requiredDocuments, setRequiredDocuments] = useState<
    RequiredDocument[]
  >([]);
  const [fetchedDocuments, setFetchedDocuments] = useState<DocumentInfo[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentInfo[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [currentActiveStage, setCurrentActiveStage] = useState<any>();

  const [isDocsLoading, setIsDocsLoading] = useState<boolean>(false);
  const lastDocsSignature = useRef<string>("");

  const showDrawer = (): void => {
    setIsDrawerVisible(true);
  };

  useEffect(() => {
    if (activeStage) {
      const currentStage = preAuth?.stepperData?.visitStages?.find(
        (stage: any) => stage?.visitStageId === parseInt(activeStage)
      );

      if (currentStage) {
        setCurrentActiveStage(currentStage);
        setSelectedQueryStage(
          currentStage?.visitStage + "~" + currentStage?.visitStageId
        );
      } else {
        setSelectedQueryStage("");
      }
    } else {
      setSelectedQueryStage("");
    }
  }, [preAuth?.stepperData, activeStage]);

  const schemeMetaDataId =
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
  const portalMetaDataId =
    patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];
  const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
  const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;

  const schemeSchemaKeys = [
    "casePreAuthCghs",
    "casePreAuthEchs",
    "casePreAuthIhx",
    "casePreAuthStarhealth",
    "casePreAuthVidal",
  ];

  const schemePortalData = schemeSchemaKeys.reduce((result: any, key) => {
    if (preAuth?.portalData?.[key] && key !== "casePreAuthEchs") {
      result = preAuth?.portalData?.[key];
    } else if (key === "casePreAuthEchs" && preAuth?.portalData?.[key]) {
      if (preAuth?.portalData?.[key]?.casePreAuthEchsReferralResponse) {
        result = preAuth?.portalData?.[key]?.casePreAuthEchsReferralResponse;
      } else if (preAuth?.portalData?.[key]?.casePreAuthEchsEmergencyResponse) {
        result = preAuth?.portalData?.[key]?.casePreAuthEchsEmergencyResponse;
      }
    }
    return result;
  }, {});

  const casePreAuthId = schemePortalData?.casePreAuthResponse?.id || null;

  const fetchStepperForStatus = () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      dispatch(fetchStepperData({ caseId: caseId, visitId: visitId }));
    }
  };

  const handleAddQuery = () => {
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    if (requiredDocuments?.length > 0 
      // || selectedQueryStage?.split("~")?.[0] === "Portal registration"
    ) {
      if (caseId && visitId && selectedQueryStage) {
        const queryData: IPreAuthQuery = {
          queryStage: selectedQueryStage?.split("~")?.[0],
          stageId: parseInt(selectedQueryStage?.split("~")?.[1]) || 0,
          queryQuestion: queryText,
          typeOfQuery: typeOfQuery,
          assignToFrontOffice: assignToFrontOffice === "YES" ? true : false, // from Radio.Group
          requiredDocuments: requiredDocuments, // directly use the existing state,
          casePreAuthId: casePreAuthId,
          queryType: "Manual",
        };

        setIsSubmitting(true);
        dispatch(addQuery({ queryData, caseId, visitId, portalMetaDataId }))
          .then((result) => {
            if (addQuery.fulfilled.match(result)) {
              setQueryText("");
              setSelectedQueryStage("");
              setAssignToFrontOffice("");
              closeCreateQueryModal();
              fetchStepperForStatus();
              setRequiredDocuments([]);
              dispatch(
                getTimelineDetails({
                  caseId,
                  visitId,
                })
              );
              dispatch(
                fetchQueries({
                  caseId,
                  visitId,
                  portalMetaDataId,
                  stageId: [parseInt(selectedQueryStage?.split("~")?.[1])],
                })
              );
              if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
                dispatch(
                  getBasicDetails({
                    id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
                  })
                );
              }
              setIsSubmitting(false);
            } else if (addQuery.rejected.match(result)) {
              errorMessageModal(
                (result?.payload || "Please, Try again later") as string
              );
              setIsSubmitting(false);
            }
          })
          .catch((error) => {
            // Handle error
            console.error("Failed to add query", error);
            setIsSubmitting(false);
          });
      } else if (!selectedQueryStage) {
        errorMessageModal("Please, Select Stage first");
        setIsSubmitting(false);
      }
    } else {
      errorMessageModal("Please, Request Documents before creating query");
    }
  };

  // useEffect(() => {
  //   if (portalMetaDataId && schemeMetaDataId) {
  //     const fetchDocuments = async () => {
  //       // setLoading(true);
  //       try {
  //         const data = await getDocuments(portalMetaDataId, schemeMetaDataId);
  //         const uniqueDocuments = Object.values(data)
  //           .flat()
  //           ?.filter(
  //             (doc: any, index: number, self: any[]) =>
  //               index === self?.findIndex((t: any) => t.label === doc.label)
  //           )
  //           ?.map((doc: any) => ({ id: doc.value, value: doc.label }));
  //         setFetchedDocuments(uniqueDocuments);
  //         // setFilteredDocuments(uniqueDocuments);
  //       } catch (err) {
  //         message.error("Failed to fetch documents.");
  //       }
  //     };

  //     fetchDocuments();
  //   }
  // }, [portalMetaDataId, schemeMetaDataId]);

  useEffect(() => {
    if (caseId && visitId && schemeMetaDataId) {
      fetchDocuments();
    }
  }, [caseId, visitId, schemeMetaDataId, currentActiveStage]);

  const fetchDocuments = React.useCallback(async () => {
    // if (isDocsLoading) return; // Preventing API call if already in progress
    setIsDocsLoading(true);

    try {
      if (!caseId || !visitId || !schemeMetaDataId) return;
      const data = await getAllStarhealthDocumentsData(
        caseId,
        visitId,
        schemeMetaDataId
      );
      const uniqueDocuments = data?.[currentActiveStage?.visitStage]?.map(
        (doc: any) => ({ id: doc.labelId, value: doc.label })
      );
      if (
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataNames?.[0]?.toUpperCase() ===
          "CGHS" ||
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataNames?.[0]?.toUpperCase() ===
          "ECHS"
        // && patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        //   "OP"
      ) {
        const filteredBillSummaryDocument = uniqueDocuments?.filter(
          (doc: any) => doc?.value !== "BILL SUMMARY"
        );
        setFetchedDocuments(filteredBillSummaryDocument);
      } else {
        setFetchedDocuments(uniqueDocuments);
      }
      // setFetchedDocuments(uniqueDocuments);

      //for old one
      // setFilteredDocuments(uniqueDocuments);
    } catch (err) {
      message.error("Failed to fetch documents.");
    } finally {
      setIsDocsLoading(false);
    }
  }, [isDocsLoading, caseId, visitId, schemeMetaDataId, currentActiveStage]);

  const addDocument = (document: DocumentInfo): void => {
    if (!requiredDocuments?.some((doc) => doc.name === document.value)) {
      const newDoc: RequiredDocument = {
        id: document.id, // Use the document's ID
        name: document.value,
        expiryDate: "",
      };
      setRequiredDocuments([...requiredDocuments, newDoc]);
    }
  };

  const removeDocument = (name: string): void => {
    setRequiredDocuments(requiredDocuments?.filter((doc) => doc.name !== name));
  };

  const updateExpiryDate = (id: number | string, expiryDate: string): void => {
    setRequiredDocuments(
      requiredDocuments?.map((doc) =>
        doc.id === id ? { ...doc, expiryDate } : doc
      )
    );
  };

  const updateRemarks = (id: number | string, remarks: string): void => {
    setRequiredDocuments(
      requiredDocuments?.map((doc) =>
        doc.id === id ? { ...doc, remarks } : doc
      )
    );
  };

  const drawerColumns = [
    {
      title: "Document Name",
      dataIndex: "value",
      key: "value",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Add/Remove",
      key: "action",
      render: (_: any, record: DocumentInfo) => (
        <Space>
          {requiredDocuments.some((d) => d.name === record.value) ? (
            <Button
              type="text"
              icon={<MinusOutlined />}
              onClick={() => removeDocument(record.value)}
            />
          ) : (
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={() => addDocument(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (searchTerm.length >= 4) {
      const filtered = fetchedDocuments?.filter((doc) =>
        // Adjust this filter based on which document properties you want to search
        doc.value.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDocuments(filtered);
    } else {
      setFilteredDocuments(fetchedDocuments);
    }
  }, [searchTerm, fetchedDocuments]);

  return (
    <div>
      <Modal
        // title="Add Query"
        open={createQuery}
        onCancel={closeCreateQueryModal}
        footer={null}
        width={800}
        centered
        bodyStyle={{ padding: 20 }}
        className="create-query-modal"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            // padding: "23px",
            // borderRadius: "8px",
            // border: "1px solid #D9D9D9",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <label style={{ width: "30%", fontWeight: "600" }}>
              Type of Query:
            </label>
            <Radio.Group
              defaultValue="Internal"
              value={typeOfQuery}
              onChange={(e) => setTypeOfQuery(e.target.value)}
            >
              <Radio
                value="Internal"
                className={`custom-radio ${
                  typeOfQuery === "Internal" ? "selected" : ""
                }`}
              >
                Internal
              </Radio>
              <Radio
                value="Payer"
                className={`custom-radio ${
                  typeOfQuery === "Payer" ? "selected" : ""
                }`}
              >
                Payer
              </Radio>
            </Radio.Group>
          </div>
          <label style={{ width: "30%", fontWeight: "600" }}>Query:</label>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <TextArea
              rows={5}
              placeholder="Enter your query..."
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
            />
          </div>

          <div>
            <Button
              type="primary"
              style={{
                backgroundColor: "#D51E1E",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "30%",
              }}
              onClick={showDrawer}
            >
              Request Documents
            </Button>
          </div>

          <div
            style={{
              maxHeight: "250px", // Set a maximum height for 5 items (adjust as needed)
              overflowY: "auto", // Enable vertical scrolling
              border:
                requiredDocuments.length > 0 ? "1px solid #d9d9d9" : "none",

              paddingBottom: "19px",
              paddingTop: "12px",
              paddingLeft: "10px",
              paddingRight: "10px",
            }}
          >
            {requiredDocuments?.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 0",
                  // borderBottom: "1px solid #d9d9d9",
                  paddingLeft: "20px",
                }}
              >
                <span style={{ flex: 2, fontWeight: "600" }}>{doc.name}</span>

                <Input
                  style={{ width: "40%" }}
                  placeholder="Remarks"
                  onChange={(e) => updateRemarks(doc.id, e.target.value)}
                />
                {/* <Select
                  style={{ width: "40%" }}
                  placeholder="Select Expiry"
                  onChange={(value) => updateExpiryDate(doc.id, value)}
                >
                  <Option value="30min">Within 30 Minutes</Option>
                  <Option value="1hr">Within 1 Hour</Option>
                  <Option value="2hr">Within 2 Hour</Option>
                  <Option value="5h">Within 5 Hour</Option>
                  <Option value="8h">Within 8 Hour</Option>
                  <Option value="24h">Within 24 Hour</Option>
                </Select> */}
                <Button
                  type="text"
                  icon={<MinusOutlined />}
                  onClick={() => removeDocument(doc.name)}
                />
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "15px",
              marginTop: "20px",
            }}
          >
            <Button
              onClick={closeCreateQueryModal}
              style={{
                borderColor: "#004C8F",
                color: "#004C8F",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "30%",
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              style={{
                backgroundColor: "#004C8F",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "30%",
              }}
              onClick={handleAddQuery}
              loading={isSubmitting}
            >
              Add Query
            </Button>
          </div>
        </div>
      </Modal>

      <Drawer
        title="Documents List"
        placement="right"
        open={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        width={400}
      >
        <Input.Search
          placeholder="Search Documents"
          // onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
          allowClear
        />
        <Table
          dataSource={filteredDocuments}
          rowKey="id"
          columns={drawerColumns}
          pagination={false}
          onRow={(record, index: any) => ({
            style: {
              backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
            },
          })}
        />
      </Drawer>
    </div>
  );
};

export default QueryIhx;
